import { useCustomToast } from '@/hooks/use-custom-toast';
import { memo, useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import {
  TUpdateLabelCaseValidation,
  UpdateLabelCaseValidationSchema,
  useUpdateLabelCaseMutation,
} from '../../../api/update-label-case.api';
import { zodResolver } from '@hookform/resolvers/zod';
import { axiosExtractErrorMessage } from '@/lib/axios';
import { queryClient } from '@/lib/react-query';
import { GET_LIST_LABEL_CASE_QUERY_KEY } from '../../../api/get-list-label-case.api';
import { useCaseModuleModalRootActionsStore } from '@/features/cases/_shared/components/case-modal-root/case-modal-root.store';
import {
  Modal,
  ModalHeader,
  ModalTitle,
  ModalContent,
  ModalFooter,
  ModalConfirmButton,
  ModalCloseButton,
} from '@/components/_ui/modal';
import { handleDomBubbleEvent } from '@/utils/dom';
// import { GENERAL_LABEL_COLOR } from '@/features/settings/label/_shared/constants/general-label-color';
import { GENERAL_LABEL_COLOR } from '@/features/cases/_shared/constants/cases.contants';
import { cn } from '@/utils/cn';
import { Input } from '@/components/_ui/input';
import { Spinner } from '@/components/_ui/spinner';

export type TUpdateLabelCaseModalType = 'UpdateLabelCaseModal';
export interface IUpdateLabelCaseModalProps {
  labelCaseId: string;
  name?: string;
  color?: string;
}
const UpdateLabelCaseModal = memo(({ labelCaseId, name, color }: IUpdateLabelCaseModalProps) => {
  const caseModuleModalRootActions = useCaseModuleModalRootActionsStore();

  const isOpenUpdateLabelCaseModal = useMemo(() => {
    return caseModuleModalRootActions.checkIsOpenModal('UpdateLabelCaseModal');
  }, [caseModuleModalRootActions]);

  const showToast = useCustomToast();
  const form = useForm<TUpdateLabelCaseValidation>({
    resolver: zodResolver(UpdateLabelCaseValidationSchema),
    mode: 'onChange',
    defaultValues: {
      labelCaseId: '',
      name: '',
      color: '',
    },
  });

  useEffect(() => {
    if (labelCaseId || name || color) {
      form.reset({
        labelCaseId: labelCaseId || '',
        name: name || '',
        color: color || '',
      });
    }
  }, [labelCaseId, name, color]);

  const mutationUpdateLabelCase = useUpdateLabelCaseMutation({
    mutationConfigOptions: {
      onError: (_error) => {
        const errorMessage = axiosExtractErrorMessage(_error) || 'Error update label case, please try again';
        showToast({
          message: errorMessage,
          type: 'error',
        });
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [GET_LIST_LABEL_CASE_QUERY_KEY] });
        caseModuleModalRootActions.closeModal('UpdateLabelCaseModal');
        form.reset();
        showToast({
          message: 'Label case update successfully',
          type: 'success',
        });
      },
    },
  });

  const handleSubmitUpdateLabelCase = useCallback(
    (data: TUpdateLabelCaseValidation) => {
      mutationUpdateLabelCase.mutate({
        labelCaseId: data?.labelCaseId || '',
        name: data?.name,
        color: data?.color,
      });
    },
    [mutationUpdateLabelCase]
  );

  const handleClose = useCallback(() => {
    caseModuleModalRootActions.closeModal('UpdateLabelCaseModal');
    form.reset();
  }, [caseModuleModalRootActions, form]);

  return (
    <Modal isOpen={isOpenUpdateLabelCaseModal} lockBodyScroll onClose={handleClose} className="p-[32px] w-[500px]">
      <ModalHeader>
        <ModalTitle>Edit Label Case</ModalTitle>
      </ModalHeader>
      <ModalContent>
        <form
          onSubmit={(e) => {
            handleDomBubbleEvent(e);
            form.handleSubmit(handleSubmitUpdateLabelCase)(e);
          }}
          id="edit-label-case-form"
          className="grid gap-[16px]"
        >
          <div className="grid gap-[4px]">
            <label htmlFor="name-label-case" className="text-[#4B4B4B] text-[14px] font-normal">
              Name*
            </label>
            <Input id="name-label-case" placeholder="Enter Label Case Name" {...form.register('name')} />
            {form.formState.errors.name && (
              <p className="text-red-500 text-[12px] font-normal">{form.formState.errors.name.message}</p>
            )}
          </div>

          <div className="grid gap-[4px]">
            <label htmlFor="color-label-case" className="text-[#4B4B4B] text-[14px] font-normal">
              Color*
            </label>
            <div id="color-label-case" className="grid grid-cols-3 gap-[22px]">
              {Object.entries(GENERAL_LABEL_COLOR).map(([key, color], index) => {
                const isSelected = form.watch('color') === color;
                return (
                  <button
                    type="button"
                    onClick={(e) => {
                      handleDomBubbleEvent(e);
                      form.setValue('color', color);
                    }}
                    key={index + 1}
                    className={cn(
                      'w-full gap-[6px] flex justify-start py-[8px] px-[6px] rounded-[8px] bg-[#F1F5F9] cursor-pointer border-transparent border-2 hover:border-[#0C8DBF] hover:bg-[#0C8DBF]/20 transition-all duration-300',
                      isSelected && 'border-[#0C8DBF] bg-[#0C8DBF]/20'
                    )}
                  >
                    <div className="w-[16px] h-[16px] rounded-full " style={{ backgroundColor: color }}></div>
                    <span className="text-[#25151E] text-[12px] font-normal">{key}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </form>
      </ModalContent>
      <ModalFooter>
        <ModalCloseButton
          onClick={(e) => {
            handleDomBubbleEvent(e);
            handleClose();
          }}
        >
          Cancel
        </ModalCloseButton>
        <ModalConfirmButton form="edit-label-case-form" type="submit" disabled={mutationUpdateLabelCase.isPending}>
          {mutationUpdateLabelCase.isPending ? <Spinner isLoading={true} className="w-[18px] h-[18px]" /> : null} Save
        </ModalConfirmButton>
      </ModalFooter>
    </Modal>
  );
});

UpdateLabelCaseModal.displayName = 'UpdateLabelCaseModal';
export default UpdateLabelCaseModal;
